import { useEffect } from 'react';
import { MutateOptions, useMutation } from 'react-query';

import { USER_SERVICE_URLS } from '~/Constants/Urls.constants';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { Segment } from '~/Helpers/Segment.helper';
import { routeReplacer } from '~/Helpers/String.helper';
import { objectToFormData } from '~/Helpers/Url.helpers';
import { useLocalStorage, useProvider } from '~/Hooks';
import { AuthModel } from '~/Models/DTO/Auth.model';
import { IErrorResponse } from '~/Models/DTO/Error.models';

interface IAuth {
  username: string;
  password: string;
}

export const useAuth = () => {
  const { currentProvider } = useProvider();
  const { isLoading, error, data, isError, mutate } = useMutation(async (objData: IAuth) =>
    requestReactQuery<AuthModel>({
      url: routeReplacer(USER_SERVICE_URLS.ACCESS_TOKEN, {
        domain: currentProvider?.domain || '',
      }),
      data: objectToFormData({
        ...objData,
        grant_type: 'password',
        client_id: 'pro',
      }),
      config: { method: 'POST' },
    }),
  );

  const {
    isLoading: isLoginWithNoTokenLoading,
    isError: isLoginWithNoTokenError,
    error: loginWithNoTokenError,
    mutate: loginWithNoToken,
  } = useMutation(async (data: IAuth) =>
    requestReactQuery<AuthModel>({
      url: routeReplacer(USER_SERVICE_URLS.ACCESS_TOKEN, {
        domain: currentProvider?.domain || '',
      }),
      data: objectToFormData({
        ...data,
        grant_type: 'password',
        client_id: 'pro',
      }),
      config: { method: 'POST' },
    }),
  );

  const {
    isError: isRefreshTokenError,
    isLoading: isRefreshTokenLoading,
    error: refreshTokenError,
    data: refreshTokenData,
    mutate: refreshToken,
  } = useMutation(async (refreshToken: string) =>
    requestReactQuery<AuthModel>({
      url: routeReplacer(USER_SERVICE_URLS.ACCESS_TOKEN, {
        domain: currentProvider?.domain || '',
      }),
      data: objectToFormData({
        grant_type: 'refresh_token',
        client_id: 'pro',
        refresh_token: refreshToken,
      }),
      config: { method: 'POST' },
    }),
  );

  const { setAccessToken, setRefreshToken, setExpirationToken } = useLocalStorage();

  const getExpirationTime = (expiresAt: number | undefined, expiresIn: number | undefined) => {
    if (expiresAt) {
      return expiresAt;
    }
    if (expiresIn) {
      return Date.now() + expiresIn * 1000;
    }
    return 0;
  };

  useEffect(() => {
    if (refreshTokenData) {
      setAccessToken(refreshTokenData.access_token);
      setRefreshToken(refreshTokenData.refresh_token);
      setExpirationToken(
        getExpirationTime(refreshTokenData.expires_at, refreshTokenData.expires_in),
      );
    } else if (data) {
      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      setExpirationToken(getExpirationTime(data.expires_at, data.expires_in));
    }
  }, [data, refreshTokenData]);

  const login = (
    variables: IAuth,
    options?: MutateOptions<AuthModel, unknown, IAuth, unknown> | undefined,
  ) => {
    mutate(variables, options);
    Segment.track('login');
  };

  return {
    login,
    loginWithNoToken,
    isLoginWithNoTokenError,
    isLoginWithNoTokenLoading,
    loginWithNoTokenError: loginWithNoTokenError as IErrorResponse,
    isError: isError || isRefreshTokenError,
    isLoading: isLoading || isRefreshTokenLoading,
    error: (error || refreshTokenError) as IErrorResponse,
    data,
    refreshToken,
  };
};
